// Display only the existing v6 reviewed object; no clinical inference or fetching.
import { normalizeSupportiveCareScope } from './clinicalInteraction';
import { normalizeResources } from './resourceConsultation';
export const REVIEWED_CONTRACT = 'woundos-single-object-consultation-shadow-v1';
const id = (value) => typeof value === 'string' && /^[A-Za-z][A-Za-z0-9_-]{0,47}$/.test(value);
const text = (value, limit = 6000) => typeof value === 'string' && value.trim()
  && value.length <= limit * 2 && Array.from(value).length <= limit;
const object = (value) => value && typeof value === 'object' && !Array.isArray(value);
const kinds = new Set(['immediate_safety', 'clarification', 'diagnostic', 'treatment', 'follow_up', 'referral', 'supportive_care']);
const roles = new Set(['clinical_primary', 'clinical_reference', 'device_ifu', 'payer', 'unclassified', 'code_definition']);
const QUALIFICATION_CONTRACT = 'woundos-source-qualification-v1';
function rows(value, limit) {
  return Array.isArray(value) && value.length <= limit && value.every(object);
}
function uniqueIds(values) {
  return values.every((v) => id(v.id)) && new Set(values.map((v) => v.id)).size === values.length;
}
function references(value, allowed) {
  return Array.isArray(value) && value.length <= 256 && new Set(value).size === value.length
    && value.every((ref) => id(ref) && (!allowed || allowed.has(ref)));
}
function safeLink(value) {
  if (!text(value, 2000) || /\s/u.test(value)
      || Array.from(value).some((char) => char.codePointAt(0) <= 31 || char.codePointAt(0) === 127)) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

function validQualification(source, contract) {
  const fields = ['qualification_id', 'qualification_manifest_sha256', 'qualification_scope'];
  if (source.role !== 'clinical_reference' && !fields.some((key) => Object.hasOwn(source, key))) return true;
  return contract === QUALIFICATION_CONTRACT && id(source.qualification_id)
    && typeof source.qualification_manifest_sha256 === 'string'
    && /^[0-9a-f]{64}$/.test(source.qualification_manifest_sha256)
    && source.qualification_scope === 'identity_and_exact_text_only'
    && text(source.source_publisher, 300) && text(source.source_edition, 300)
    && text(source.source_locator, 300);
}


const REFERRAL_CONTRACT = 'woundos-reviewed-referrals-v1';
function validReferrals(payload, actions, sources) {
  const projected = actions.filter((a) => a.kind === 'referral');
  const supplied = payload.referrals;
  if (!projected.length && !Object.hasOwn(payload, 'referral_contract_version')) {
    return supplied === undefined || (Array.isArray(supplied) && supplied.length === 0);
  }
  if (!projected.length || payload.referral_contract_version !== REFERRAL_CONTRACT
      || !rows(supplied, 64) || supplied.length !== projected.length || !uniqueIds(supplied)) return false;
  const facts = payload.clinical_interaction?.canonical_fact_ledger?.records;
  if (!rows(facts, 256) || !uniqueIds(facts)) return false;
  const factIds = new Set(facts.map((f) => f.id));
  const clinicalIds = new Set(sources.filter((v) => ['clinical_primary', 'clinical_reference'].includes(v.role)).map((v) => v.id));
  const fields = ['id', 'kind', 'text', 'rationale', 'fact_ids', 'evidence_ids', 'uncertainty_ids'];
  const same = (a, b) => Array.isArray(a) ? Array.isArray(b)
    && a.length === b.length && a.every((v, i) => v === b[i]) : a === b;
  return projected.every((a, i) => Object.keys(a).length === fields.length
    && Object.keys(supplied[i]).length === fields.length && fields.every((k) => Object.hasOwn(a, k)
      && Object.hasOwn(supplied[i], k) && same(a[k], supplied[i][k]))
    && a.fact_ids.length > 0 && references(a.fact_ids, factIds)
    && a.evidence_ids.some((ref) => clinicalIds.has(ref)));
}

function validSupportiveCare(payload, interaction, actions, sources) {
  const scope = normalizeSupportiveCareScope(interaction);
  if (scope === null) return false;
  const projected = actions.filter((a) => a.kind === 'supportive_care');
  const supplied = payload.supportive_care;
  if (actions.some((a) => a.kind !== 'supportive_care' && a.supportive_domain != null)) return false;
  if (!scope.length) return !projected.length && (supplied === undefined
    || (Array.isArray(supplied) && supplied.length === 0));
  if (!Array.isArray(supplied) || supplied.length !== projected.length
      || supplied.some((v, i) => v !== projected[i].text)) return false;
  const facts = payload.clinical_interaction?.canonical_fact_ledger?.records;
  if (!rows(facts, 256) || !uniqueIds(facts)) return false;
  const factIds = new Set(facts.map((f) => f.id));
  const clinicalIds = new Set(sources.filter((v) => ['clinical_primary', 'clinical_reference'].includes(v.role)).map((v) => v.id));
  return projected.every((a) => scope.includes(a.supportive_domain)
    && a.fact_ids.length > 0 && references(a.fact_ids, factIds)
    && a.evidence_ids.some((ref) => clinicalIds.has(ref)));
}

export function normalizeReviewedConsultation(payload, interaction) {
  if (!Object.hasOwn(payload, 'reviewed_actions')) return undefined;
  if (payload.consultation_contract_version !== REVIEWED_CONTRACT || !interaction
      || payload.clinical_use_authorized !== false || payload.evaluation_mode !== 'shadow_only') return null;
  const actions = payload.reviewed_actions;
  const sources = payload.citations;
  const decisions = payload.open_decisions;
  const differential = payload.differential;
  if (!rows(actions, 64) || !actions.length || !rows(sources, 128) || !rows(decisions, 64)
      || !rows(differential, 24) || !uniqueIds(actions) || !uniqueIds(sources)
      || !uniqueIds(decisions) || !uniqueIds(differential)) return null;
  const sourceIds = new Set(sources.map((v) => v.id));
  const decisionIds = new Set(decisions.map((v) => v.id));
  if (sources.some((v) => !text(v.title, 300) || !text(v.source, 1000) || !roles.has(v.role)
      || !validQualification(v, payload.evidence_qualification_contract_version))) return null;
  if (differential.some((v) => v.certainty !== 'alternative' || !text(v.text))) return null;
  if (decisions.some((v) => !text(v.text) || !text(v.decision_consequence)
      || typeof v.blocks_treatment !== 'boolean')) return null;
  if (actions.some((v) => !kinds.has(v.kind) || !text(v.text) || !text(v.rationale)
      || !references(v.fact_ids) || !references(v.evidence_ids, sourceIds)
      || !references(v.uncertainty_ids, decisionIds)
      || (v.kind === 'treatment' && !interaction.treatment_plan_available))) return null;
  if (!validReferrals(payload, actions, sources)
      || !validSupportiveCare(payload, interaction, actions, sources)) return null;
  const resources = normalizeResources(payload, interaction);
  if (resources === null) return null;
  return {
    resources,
    actions: actions.map((v) => ({ id: v.id, kind: v.kind, text: v.text, rationale: v.rationale,
      ...(v.kind === 'supportive_care' ? { supportive_domain: v.supportive_domain } : {}),
      evidence_ids: [...v.evidence_ids], uncertainty_ids: [...v.uncertainty_ids] })),
    differential: differential.map((v) => ({ id: v.id, text: v.text })),
    decisions: decisions.map((v) => ({ id: v.id, text: v.text,
      consequence: v.decision_consequence, blocks_treatment: v.blocks_treatment })),
    sources: sources.map((v) => ({ id: v.id, title: v.title, source: v.source,
      role: v.role, url: safeLink(v.url),
      ...(v.qualification_id ? { publisher: v.source_publisher, edition: v.source_edition,
        locator: v.source_locator, qualification_scope: v.qualification_scope } : {}) })),
  };
}
